import React, { useEffect, useRef } from 'react';

import { AppBar, makeStyles, Slide, Toolbar, useScrollTrigger } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReactComponent as LogoLight } from 'assets/logo_light.svg';
import ContentSection from 'components/Layout/_components/sections/ContentSection/ContentSection';
import DashboardsSection from 'components/Layout/_components/sections/DashboardsSection/DashboardsSection';
import FooterSection from 'components/Layout/_components/sections/FooterSection/FooterSection';
import OrganizationSection from 'components/Layout/_components/sections/OrganizationSection/OrganizationSection';
import PocketSafeAdministrationSection from 'components/Layout/_components/sections/PocketSafeAdministrationSection/PocketSafeAdministrationSection';
import ServiceSection from 'components/Layout/_components/sections/ServiceSection/ServiceSection';
import UserSection from 'components/Layout/_components/sections/UserSection/UserSection';
import isIOS from 'helpers/isIOS';
import menu_messages from 'messages/menu_messages';
import { useAuthContext } from 'reactContext/AuthContext';
import PATHS from 'router/PATHS';
import useBrandingStore from 'storages/brandingStore';
import useLayoutStore from 'storages/layoutStore';

const MENU_BACKGROUND = '#101828';
const MENU_BACKGROUND_SOFT = '#14233A';
const MENU_BACKGROUND_DEEP = '#0C1727';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  bar: {
    background: `linear-gradient(90deg, ${MENU_BACKGROUND} 0%, ${MENU_BACKGROUND_SOFT} 100%)`,
    color: '#F8FBFF',
    ...theme.mixins.toolbar,
    borderBottom: `1px solid ${theme.palette.accents.separator}`,
    boxShadow: '0 10px 24px rgba(16, 24, 40, 0.18)',
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbar: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    justifyItems: 'flex-start',
    ...theme.mixins.toolbar,
  },
  logo: {
    width: '102px',
    height: '16px',
  },
  icon: {
    color: '#F8FBFF',
  },
  closeButton: {
    color: '#6DD3B2',
    border: '1px solid #6DD3B2',
    borderRadius: '100%',
    padding: '5px',
    margin: '5px',
  },
  focusableControl: {
    '&.Mui-focusVisible, &:focus-visible': {
      outline: `3px solid ${theme.palette.grey[300]}`,
      outlineOffset: 2,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  drawer: {
    minHeight: '100vh',
    display: 'flex',
    '-webkit-overflow-scrolling': 'touch',
    background: `linear-gradient(180deg, ${MENU_BACKGROUND} 0%, ${MENU_BACKGROUND_SOFT} 58%, ${MENU_BACKGROUND_DEEP} 100%)`,
    color: '#F8FBFF',
    ...theme.mixins.toolbarPadding,
    '&.MuiDrawer-paper, & .MuiDrawer-paper': {
      background: `linear-gradient(180deg, ${MENU_BACKGROUND} 0%, ${MENU_BACKGROUND_SOFT} 58%, ${MENU_BACKGROUND_DEEP} 100%)`,
      color: '#F8FBFF',
    },
  },
  drawerInner: {
    flexGrow: '1',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    background: 'transparent',
  },
  drawerMainSection: {
    flexGrow: '1',
    marginBottom: '-2px',
    background: 'transparent',
  },
}));

const MainMenuMobile = () => {
  const { t } = useTranslation();
  const { logo } = useBrandingStore();
  const bodyScrollLockElement = useRef();

  const { isAuthorized } = useAuthContext();
  const trigger = useScrollTrigger();

  const { isMenuOpen, closeMenu, toggleMenu } = useLayoutStore();

  useEffect(() => {
    if (!isIOS()) {
      if (isMenuOpen) disableBodyScroll(bodyScrollLockElement);
      else enableBodyScroll(bodyScrollLockElement);
    }
    return () => enableBodyScroll(bodyScrollLockElement);
  }, [isMenuOpen]);

  useEffect(() => {
    closeMenu();
  }, []);

  const styles = useStyles();
  return (
    <div className={styles.root}>
      <Slide appear={false} direction='down' in={!trigger || isMenuOpen}>
        <AppBar className={styles.bar}>
          <Toolbar className={styles.toolbar}>
            <IconButton
              aria-label={t(menu_messages.go_to_website)}
              className={styles.focusableControl}
              component='a'
              href={process.env.REACT_APP_LANDING_PAGE_URL}
              target='_blank'
            >
              {logo?.full ? <img alt='' className={styles.logo} src={logo?.full} /> : <LogoLight className={styles.logo} />}
            </IconButton>
            <IconButton
              aria-label={t(menu_messages.global_search)}
              className={styles.focusableControl}
              component={Link}
              to={PATHS.GLOBAL_SEARCH}
            >
              <SearchIcon className={styles.icon} />
            </IconButton>
            <IconButton
              aria-controls='mobile-main-navigation'
              aria-expanded={isMenuOpen}
              aria-label={t(isMenuOpen ? menu_messages.close_navigation : menu_messages.open_navigation)}
              className={clsx(styles.focusableControl, isMenuOpen ? styles.closeButton : styles.icon)}
              onClick={toggleMenu}
            >
              {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>
      </Slide>
      <Drawer
        ref={bodyScrollLockElement}
        anchor='top'
        classes={{
          paper: styles.drawer,
        }}
        open={isMenuOpen}
        variant='persistent'
      >
        <div className={styles.drawerInner}>
          <nav aria-label={t(menu_messages.main_navigation)} className={styles.drawerMainSection} id='mobile-main-navigation'>
            <UserSection />
            {isAuthorized && isMenuOpen && (
              <>
                <ServiceSection />
                <OrganizationSection />
                <ContentSection />
                <DashboardsSection />
                <PocketSafeAdministrationSection />
              </>
            )}
          </nav>
          <FooterSection />
        </div>
      </Drawer>
    </div>
  );
};

MainMenuMobile.propTypes = {};

export default MainMenuMobile;
